import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Play, FileText, MessageCircle, ChevronDown, ChevronUp, ExternalLink, Calendar, Clock, User } from 'lucide-react';
import Link from '../components/Link';

interface StudyData {
  id: string;
  title: string;
  description: string;
  youtube_url: string;
  thumbnail: string;
  date: string;
  duration: string;
  study_notes: {
    visible: boolean;
    content: string;
  };
  speaker: string;
}

export default function Study() {
  // const [searchParams] = useSearchParams();
  // const studyId = searchParams.get('id');
  const [studyData, setStudyData] = useState<StudyData | null>(null);
  const [showNotes, setShowNotes] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    handleStartUp();
  }, []);

  const handleStartUp = () => {
    const searchParams = new URLSearchParams(window.location.search);
    const id = searchParams.get("id");

    if (!id) {
      alert("no Id");
      setLoading(false)
      return;
    }

    fetchLibraryItem(id)

  }

  const fetchLibraryItem = async (id: any) => {
    const endpoint = `https://tsoministries.com/wp-json/wp/v2/library/${id}`;
    try {
      const response = await fetch(`${endpoint}`);

      if (!response.ok) {
        console.error(`Error fetching data`);
        setStudyData(null);
      } else {
        const data = await response.json();
        setStudyData(data);
        setLoading(false);
      }

    } catch (error) {
      console.error('Failed to fetch library items:', error);
      setStudyData(null);
      setLoading(false);
    }
  };






  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-900 to-black text-white pt-24 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  if (!studyData) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-900 to-black text-white pt-24 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">Study Not Found</h1>
          <Link to="/library" className="text-orange-400 hover:text-orange-300">
            Back to Library
          </Link>
        </div>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="min-h-screen bg-gradient-to-b from-gray-900 to-black text-white pt-24"
    >
      <div className="container mx-auto px-4 py-12 max-w-4xl">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h1 className="text-4xl font-bold mb-4">{studyData.title}</h1>

        </motion.div>

        {/* YouTube Video Link */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mb-12"
        >
          <div className="relative group">
            <div className="aspect-video rounded-xl overflow-hidden bg-gray-800">
              <img
                src={studyData.thumbnail}
                alt={studyData.title}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-black/40 flex flex-col items-center justify-center group-hover:bg-black/60 transition-colors">
                <Link
                  to={`/ask?id=${studyData.id}`}
                  className="mb-1 inline-flex animate-bounce items-center gap-2 bg-gradient-to-r from-orange-600 to-blue-600 hover:from-orange-700 hover:to-blue-700 px-8 py-4 rounded-full text-white font-medium transition-all duration-300"
                >
                  <MessageCircle className="w-5 h-5" />
                  <span>View Questions & Ask</span>
                </Link>

                <motion.a
                  href={studyData.youtube_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 bg-red-600 hover:bg-red-700 px-8 py-4 rounded-full text-white font-medium transition-colors"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Play className="w-6 h-6" />
                  <span>Watch on YouTube</span>
                  <ExternalLink className="w-5 h-5" />
                </motion.a>
              </div>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex flex-wrap gap-4 text-sm text-gray-400 mb-6">
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              <span>{studyData.date}</span>
            </div>
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4" />
              <span>{studyData.duration}</span>
            </div>
            <div className="flex items-center gap-2">
              <User className="w-4 h-4" />
              <span>{studyData.speaker}</span>
            </div>
          </div>
          <p className="text-md text-gray-300 mb-0 font-bold">Description:</p>
          <p className="text-md text-gray-300 mb-6">{studyData.description}</p>

        </motion.div>

        {/* Study Notes Section */}
        {studyData.study_notes?.visible && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="mb-12"
          >
            <div className="bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 overflow-hidden">
              <button
                onClick={() => setShowNotes(!showNotes)}
                className="w-full flex items-center justify-between p-6 hover:bg-white/5 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <FileText className="w-6 h-6 text-amber-400" />
                  <h2 className="text-2xl font-bold">Study Notes</h2>
                </div>
                {showNotes ? (
                  <ChevronUp className="w-6 h-6 text-gray-400" />
                ) : (
                  <ChevronDown className="w-6 h-6 text-gray-400" />
                )}
              </button>

              {showNotes && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  className="border-t border-white/10"
                >
                  <div className="p-6 prose prose-invert max-w-none">
                    <pre className="whitespace-pre-wrap text-gray-300 leading-relaxed">
                      {studyData.study_notes.content}
                    </pre>
                  </div>
                </motion.div>
              )}
            </div>
          </motion.div>
        )}

        {/* Q&A Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="mb-12"
        >
          <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10">
            <div className="flex items-center gap-3 mb-6">
              <MessageCircle className="w-6 h-6 text-orange-400" />
              <h2 className="text-2xl font-bold">Questions & Answers</h2>
            </div>

            <p className="text-gray-300 mb-6">
              Have questions about this study? Join the discussion and get answers from our community and ministry team.
            </p>

            <Link
              to={`/ask?id=${studyData.id}`}
              className="inline-flex animate-bounce items-center gap-2 bg-gradient-to-r from-orange-600 to-blue-600 hover:from-orange-700 hover:to-blue-700 px-6 py-3 rounded-lg text-white font-medium transition-all duration-300"
            >
              <MessageCircle className="w-5 h-5" />
              <span>View Questions & Ask</span>
            </Link>
          </div>
        </motion.div>

        {/* Navigation */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
          className="flex justify-center"
        >
          <Link
            to="/library"
            className="text-gray-400 hover:text-white transition-colors"
          >
            ← Back to Library
          </Link>
        </motion.div>
      </div>
    </motion.div>
  );
}